import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import Topbar from "../../../components/topbar/TopBar";
import { getPricingVehicleByIdUseCase } from "../domain/usecases/getPricingVehicleByIdUseCase";
import { updatePricingVehicleUseCase } from "../domain/usecases/updatePricingVehicleUseCase";

const SERVICE_BOOKING_OPTIONS = [
  { value: 1, label: "Now" },
  { value: 2, label: "Later" },
];

function createForm(vehicle) {
  return {
    id: vehicle?.id || "",
    name: vehicle?.name || "",
    description: vehicle?.description || "",
    info: vehicle?.info ?? "",
    maxPersonNational:
      vehicle?.maxPersonNational === null ||
      vehicle?.maxPersonNational === undefined
        ? "1"
        : String(vehicle.maxPersonNational),
    maxPersonInternational:
      vehicle?.maxPersonInternational === null ||
      vehicle?.maxPersonInternational === undefined
        ? "1"
        : String(vehicle.maxPersonInternational),
    serviceBooking: vehicle?.serviceBooking || 1,
    priceBase:
      vehicle?.priceBase === null || vehicle?.priceBase === undefined
        ? "5.00"
        : String(vehicle.priceBase),
    multiplier:
      vehicle?.multiplier === null || vehicle?.multiplier === undefined
        ? "1.00"
        : String(vehicle.multiplier),
    active: Boolean(vehicle?.active),
  };
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return (
    response?.success === true ||
    message.includes("success") ||
    message.includes("updated")
  );
}

export const PricingVehicleUpdatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(createForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  useEffect(() => {
    const loadPricingVehicle = async () => {
      try {
        setLoading(true);
        const pricingVehicle = await getPricingVehicleByIdUseCase(id);
        setForm(createForm(pricingVehicle));
      } catch (error) {
        setMessage({
          open: true,
          type: "error",
          text:
            error.message || "No se pudo cargar la configuracion del vehiculo.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadPricingVehicle();
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckChange = (event) => {
    setForm((prev) => ({
      ...prev,
      active: event.target.checked,
    }));
  };

  const handleSave = async () => {
    const payload = {
      id: form.id,
      name: form.name.trim(),
      description: form.description.trim(),
      info: form.info,
      maxPersonNational: Number(form.maxPersonNational),
      maxPersonInternational: Number(form.maxPersonInternational),
      serviceBooking: Number(form.serviceBooking),
      priceBase: Number(form.priceBase),
      multiplier: Number(form.multiplier),
      active: Boolean(form.active),
    };

    try {
      setSaving(true);
      const response = await updatePricingVehicleUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error(
          response?.message ||
            "No se pudo actualizar la configuracion del vehiculo.",
        );
      }

      setMessage({
        open: true,
        type: "success",
        text: "Actualizacion correcta.",
      });

      window.setTimeout(() => {
        navigate("/admin/precio-vehiculo/lista");
      }, 1200);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text:
          error.message ||
          "No se pudo actualizar la configuracion del vehiculo.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      Actualizar Vehiculo
                    </Typography>
                    <Typography
                      sx={{ mt: 0.75, color: "#fff !important", opacity: 0.88 }}
                    >
                      Modifica la capacidad y precios base del vehiculo.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/admin/precio-vehiculo/lista")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Volver
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Cargando vehiculo...
                    </Typography>
                  </Stack>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      maxWidth: 480,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                      />
                      <TextField
                        fullWidth
                        label="Descripcion"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        multiline
                        minRows={2}
                      />
                      <TextField
                        fullWidth
                        label="Informacion adicional"
                        name="info"
                        value={form.info}
                        onChange={handleChange}
                        multiline
                        minRows={3}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Maximo personas nacional"
                        name="maxPersonNational"
                        value={form.maxPersonNational}
                        onChange={handleChange}
                        inputProps={{ min: "1", step: "1" }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Maximo personas internacional"
                        name="maxPersonInternational"
                        value={form.maxPersonInternational}
                        onChange={handleChange}
                        inputProps={{ min: "1", step: "1" }}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Tipo de servicio"
                        name="serviceBooking"
                        value={form.serviceBooking}
                        onChange={handleChange}
                      >
                        {SERVICE_BOOKING_OPTIONS.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.value} - {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        type="number"
                        label="Precio base"
                        name="priceBase"
                        value={form.priceBase}
                        onChange={handleChange}
                        inputProps={{ min: "5", step: "0.01" }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Multiplicador"
                        name="multiplier"
                        value={form.multiplier}
                        onChange={handleChange}
                        inputProps={{ min: "1", step: "0.01" }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.active}
                            onChange={handleCheckChange}
                            sx={{
                              color: "#165c53",
                              "&.Mui-checked": {
                                color: "#165c53",
                              },
                            }}
                          />
                        }
                        label="Activo"
                        sx={{
                          mx: 0,
                          "& .MuiFormControlLabel-label": {
                            color: "#000",
                            fontWeight: 600,
                          },
                        }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          bgcolor: "#165c53",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#12483f",
                          },
                        }}
                      >
                        {saving ? "Actualizando..." : "Actualizar"}
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};

export default PricingVehicleUpdatePage;
